import {ApiClient} from "./apiClient";
import {ApiResponse} from "./types";
import {appConfig} from "../config";
import {UserAccount} from "./UserAccountApi";
import {BusinessAction} from "./BusinessActionApi";
import {LeaveRequest, TimeRecordRequest} from "./RequestApi";

// ── Enums ─────────────────────────────────────────────────────────────────────

export type ApprovalInstanceStatus =
    | 'PENDING'
    | 'IN_PROGRESS'
    | 'APPROVED'
    | 'REJECTED'
    | 'CANCELLED'
    | 'EXPIRED'
    | 'RETURNED';

export type StepInstanceStatus =
    | 'PENDING'
    | 'WAITING_APPROVAL'
    | 'APPROVED'
    | 'REJECTED'
    | 'SKIPPED'
    | 'TIMED_OUT'
    | 'RETURNED';

export type ApproverVote =
    | 'APPROVED'
    | 'REJECTED'
    | 'ABSTAINED'
    | 'RETURNED'
    | 'COMMENTED';

export type ParticipantRole = 'APPROVER' | 'CONTRIBUTOR' | 'CC';

export type GdprSensitivityLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'NONE';

export type EntityType =
    | 'BUSINESS_ACTION'
    | 'TIME_RECORD_REQUEST'
    | 'LEAVE_REQUEST'
    | 'OVERTIME_REQUEST'
    | 'EXPENSE_CLAIM'
    | 'SALARY_CHANGE'
    | 'PROFILE_CHANGE'
    | 'CONTRACT_CHANGE'
    | 'PAYROLL_RUN'
    | 'PAYROLL_CORRECTION'
    | 'HIRING_REQUEST'
    | 'TERMINATION_REQUEST'
    | 'POSITION_CHANGE'
    | 'BENEFIT_CHANGE';

// ── Entity type metadata ───────────────────────────────────────────────────────

export interface EntityTypeMetadata {
    label: string;
    icon: string;
}

export const ENTITY_TYPE_METADATA: Record<EntityType, EntityTypeMetadata> = {
    BUSINESS_ACTION: {label: 'Geschäftsvorgang', icon: 'work'},
    TIME_RECORD_REQUEST: {label: 'Arbeitszeitkorrektur', icon: 'more_time'},
    LEAVE_REQUEST: {label: 'Urlaubsantrag', icon: 'beach_access'},
    OVERTIME_REQUEST: {label: 'Überstundenantrag', icon: 'more_time'},
    EXPENSE_CLAIM: {label: 'Spesenabrechnung', icon: 'receipt_long'},
    SALARY_CHANGE: {label: 'Gehaltsänderung', icon: 'payments'},
    PROFILE_CHANGE: {label: 'Profiländerung', icon: 'manage_accounts'},
    CONTRACT_CHANGE: {label: 'Vertragsänderung', icon: 'description'},
    PAYROLL_RUN: {label: 'Gehaltsabrechnung', icon: 'calculate'},
    PAYROLL_CORRECTION: {label: 'Abrechnungskorrektur', icon: 'edit_note'},
    HIRING_REQUEST: {label: 'Einstellungsantrag', icon: 'person_add'},
    TERMINATION_REQUEST: {label: 'Kündigungsantrag', icon: 'person_remove'},
    POSITION_CHANGE: {label: 'Positionswechsel', icon: 'transfer_within_a_station'},
    BENEFIT_CHANGE: {label: 'Benefit-Änderung', icon: 'card_giftcard'},
};

// ── Shared structures ─────────────────────────────────────────────────────────

export interface ApprovalAction {
    id: string;
    actor: UserAccount;
    approverVote: ApproverVote;
    participantRole: ParticipantRole;
    comment?: string;
    actedAt: string;
}

export interface ApprovalStepInstance {
    id: string;
    sequenceOrder: number;
    status: StepInstanceStatus;
    actions: ApprovalAction[];
    requireRejectionComment: boolean;
}

// ── ApprovalInstance inheritance ──────────────────────────────────────────────

/**
 * Fields common to every ApprovalInstance subtype.
 * Not used directly — consume the ApprovalInstance discriminated union instead.
 */
interface ApprovalInstanceBase {
    id: string;
    entityType: EntityType;
    businessKey: string;
    status: ApprovalInstanceStatus;
    submittedAt: string;
    requestedAt: string;
    requestedBy: UserAccount;
    completedAt?: string;
    currentSequenceOrder?: number;
    contextSnapshot: Record<string, unknown>;
    gdprSensitivityLevel: GdprSensitivityLevel;
    cancellationReason?: string;
    stepInstances: ApprovalStepInstance[];
    versionLock: number;
    /**
     * Votes the current user may cast on the active step of this instance.
     * Populated server-side by ApprovalInstanceService; empty when the instance
     * is terminal, the user has no active step, or any eligibility check fails.
     * The server re-validates on every action submission.
     */
    eligibleVotes: ApproverVote[];
    /**
     * Whether the current user may cancel this instance.
     * True only for the original requester on a non-terminal instance.
     */
    canCancel: boolean;
}

export interface BusinessActionApprovalInstance extends ApprovalInstanceBase {
    entityType: 'BUSINESS_ACTION';
    approvableEntity: BusinessAction;
}

export interface TimeRecordRequestApprovalInstance extends ApprovalInstanceBase {
    entityType: 'TIME_RECORD_REQUEST';
    approvableEntity: TimeRecordRequest;
}

export interface LeaveRequestApprovalInstance extends ApprovalInstanceBase {
    entityType: 'LEAVE_REQUEST';
    approvableEntity: LeaveRequest;
}

/**
 * Catch-all for entity types that do not yet have a dedicated subtype.
 * Adding a new typed subtype above automatically excludes it from here.
 */
export interface GenericApprovalInstance extends ApprovalInstanceBase {
    entityType: Exclude<EntityType, 'BUSINESS_ACTION' | 'TIME_RECORD_REQUEST' | 'LEAVE_REQUEST'>;
}

export type ApprovalInstance =
    | BusinessActionApprovalInstance
    | TimeRecordRequestApprovalInstance
    | LeaveRequestApprovalInstance
    | GenericApprovalInstance;

// ── Type guards ───────────────────────────────────────────────────────────────

export const isBusinessActionApprovalInstance = (
    instance: ApprovalInstance
): instance is BusinessActionApprovalInstance =>
    instance.entityType === 'BUSINESS_ACTION';

export const isTimeRecordRequestApprovalInstance = (
    instance: ApprovalInstance
): instance is TimeRecordRequestApprovalInstance =>
    instance.entityType === 'TIME_RECORD_REQUEST';

export const isLeaveRequestApprovalInstance = (
    instance: ApprovalInstance
): instance is LeaveRequestApprovalInstance =>
    instance.entityType === 'LEAVE_REQUEST';

// ── Status helpers ────────────────────────────────────────────────────────────

export const isTerminalStatus = (status: ApprovalInstanceStatus): boolean =>
    status === 'APPROVED' || status === 'REJECTED' || status === 'CANCELLED' || status === 'EXPIRED';

export const isTerminalStepStatus = (status: StepInstanceStatus): boolean =>
    status === 'APPROVED' || status === 'REJECTED' || status === 'SKIPPED' || status === 'TIMED_OUT';

// ── Request / response types ──────────────────────────────────────────────────

export interface ProcessActionRequest {
    vote: ApproverVote;
    comment: string | null;
    rejectionReason: string | null;
    /**
     * Client-generated UUID checked by the engine before the actor-duplicate guard.
     * A matching key on an already-committed action returns silently (idempotent retry).
     * Generate a fresh UUID per user-initiated submit.
     */
    idempotencyKey: string;
    adminOverride: string | null;
}

export interface CancelRequest {
    reason: string | null;
}

// ── API client ────────────────────────────────────────────────────────────────

const approvalApiClient = new ApiClient(`${appConfig.backendUrl}/api/approval-instances`);

export const fetchApprovalInstance = (id: string): Promise<ApiResponse<ApprovalInstance>> =>
    approvalApiClient.get<ApprovalInstance>(`/${id}`);

/**
 * Records a vote on a specific step of an approval instance.
 * The idempotencyKey in the request body must be a client-generated UUID.
 */
export const processAction = <T extends ApprovalInstance>(
    instanceId: string,
    stepInstanceId: string,
    request: ProcessActionRequest,
): Promise<ApiResponse<T>> =>
    approvalApiClient.post<T>(
        `/${instanceId}/steps/${stepInstanceId}/actions`,
        request,
    );

/**
 * Cancels an approval instance.
 * Only the original requester may call this from the standard UI;
 * the server validates the actor from the auth context.
 */
export const cancelInstance = (
    instanceId: string,
    request: CancelRequest
): Promise<ApiResponse<ApprovalInstance>> =>
    approvalApiClient.delete<ApprovalInstance>(`/${instanceId}`, request);